import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
	Card,
	Row,
	Spinner,
	Col,
	Button,
	Form as BsForm
} from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { BillingType } from '../constants/billingType';
import { Inputs } from './inputs';
import axios from 'axios';
import { AddressForm } from '../AddressForm';
import { useMutation, useQueryClient } from 'react-query';
import { Errors, useSubmit } from '../hooks/useSubmit';
import { ContentContext } from '../Context/ContentContext';
import { checkoutButtonColors } from '../PageBlocks/appearance';
import { OrderBumpContext } from '../Context/OrderBumpContext';
import { addOrderExtra } from '../../Thanks/order-extras';
import { PaymentContext } from '../Context/PaymentContext';
import { PlanContext } from '../Context/PlanContext';
import { apiFactory } from '../../../../api/ApiFactory';
import { paymentsApi } from '../../../../api/payments';
import { membersApi } from '../../../../api/members';
import { PhoneInput } from '../../../../components/Inputs/PhoneInput';
import { CpfCnpjInput } from '../../../../components/Inputs/CpfCnpjInput';
import { ApplicationContext } from '../../../../contexts/ApplicationContext';
import {
	CheckoutPageContext,
	CheckoutPageSlotsContext,
	CheckoutPageConfirmationContext
} from '../Context/CheckoutPageContext';
import { trackPurchase } from '../PageBlocks/tracking';
import { identifyBuyer, trackBehavior } from '../PageBlocks/behavior-tracker';
import { toast } from '../../../../utils/toast';
import { getApiErrorMessage } from '../../../../utils/get-api-error-message';
import { isSellerNotActiveError } from '../../../../utils/payments-availability';
import { markPaymentsUnavailable } from '../../PaymentsUnavailable';
import {
	apiFieldErrorOf,
	isFieldMounted
} from '../../../../utils/api-field-error';
import {
	reportCheckoutError,
	reportCheckoutSubmitError
} from '../../../../utils/sentry-checkout';
import { isValidDocument } from '../../../../utils/validators/document';
import { BillingTypeForm } from '../BillingTypeForm';
import { Checkbox } from '../../../../components/Inputs/Checkbox';

import { OrderBump } from '../OrderBump';
import { PageSlotBlocks } from '../PageBlocks';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CouponContext } from '../Context/CouponContext';
import { InstallmentsInput } from '../InstallmentsInput';
import { SubscriptionContext } from '../Context/SubscriptionContext';
import { subscriptionsApi } from '../../../../api/subscriptions';
import { ShieldCheck } from '@phosphor-icons/react';
import { useGoogleAnalytics } from '../../../../hooks/Analytics';
import * as Sentry from '@sentry/react';
import { SUBMITTING_FLAG } from '../ExitIntent';
import { useIsDarkMode } from '../../../../hooks/useIsDarkMode';
import { CheckoutFormSkeleton } from '../LoadingSkeleton';
import { useFbPixel } from '../../../../hooks/FbPixel';
import { checkoutsApi } from '../../../../api/checkouts';
import { CURSEDUCA_PLANS_REFERENCES } from '../constants/plansReferences';
import {
	PIX_CHARGE_STORAGE_PREFIX,
	PIX_CONSENT_STORAGE_PREFIX
} from '../constants/pixCharge';
import { resolveSubscriptionRedirect } from '../helpers/resolveSubscriptionRedirect';
import {
	isCurrentMemberRequest,
	isMemberAlreadyExistsError,
	isSameMember,
	MemberRequest,
	normalizeEmail,
	RememberedMember
} from '../helpers/memberSession';
import { safeExternalRedirect } from '../helpers/safeExternalRedirect';
import { withSessionFragment } from '../../../../utils/memberEntryUrl';
import {
	getSuccessRedirectOptions,
	resolveSuccessRedirectStep,
	safeRedirectUrl,
	storeSuccessRedirect
} from '../helpers/successRedirect';

// Re-exported: api/index.ts, api/creditCards/tokenize.ts and Recurring have
// imported it from here since before it became its own module.
export { CURSEDUCA_PLANS_REFERENCES };

// Plan Reference -> Application Plan UUID
const CHAT_AI_PLANS_REFERENCES: Record<string, string> = {
	VGVzdGUgQ2hhdCBBSV9YX3VuZGVmaW5lZF8xNzQ3MzQ3Njk5NzI0:
		'58aab11a-31db-11f0-8e95-122383938381'
};

// Platforms that sign a fixed, platform-specific term of use instead of the
// application's current term. Keyed by api_key.
const FIXED_TERM_PLATFORMS: Record<string, { uuid: string; url: string }> = {
	// Contabilizei
	d1edfbde1f4e42c99c0f39e77ba4197dab9403f2: {
		uuid: 'b03091da-3995-11f1-8782-0affc132824b',
		url: 'https://files.curseduca.com/4bb8690d-7a4d-4115-b9c1-c3aee54f9e75/5618485d.pdf'
	},
	// Skyone
	c9efc57675874bfa2f8a3349b4c1e75f9f42a3b9: {
		uuid: 'f0b2cffb-91db-11f1-8782-0affc132824b',
		url: 'https://files.curseduca.com/8b43382c-77bc-4e77-8697-2d42270d2f67/ff6ef06e.pdf'
	}
};

// Pathnames that require Turnstile verification.
//
// `hlg` saiu em 11/09/2026: é o ambiente de HOMOLOGAÇÃO, e o widget não
// carregava lá ("Unable to connect to website"), travando o submit — a lista
// governa os DOIS lados, o render do widget e a exigência do token no envio.
// Captcha em homologação bloqueia o teste sem proteger nada.
//
// ⚠️ O casamento é por SUBSTRING (`pathname.includes` + `href.includes`), não
// por igualdade: uma entrada curta aqui liga o captcha em qualquer URL que a
// contenha. Era mais um motivo para tirar `hlg`, que é curto o bastante para
// casar por acidente.
const PATHNAMES_WITH_TURNSTILE = [
	'pbypschool',
	'plannerpatronus',
	'syhusacademy',
	'skyone',
	'boquinhas'
	// 'demo'
];

export const Form = ({ apiKey: apiKeyProp = '' }: { apiKey?: string }) => {
	const [shouldShowInstallmentInput, setShowInstallmentInput] =
		useState<boolean>(false);

	const [hasSignedTerms, setHasSignedTerms] = useState<boolean>(false);

	const navigate = useNavigate();
	const { platform } = useParams<{ platform?: string }>();
	const [searchParams] = useSearchParams();

	const prefillKey = 'checkout-prefill:' + window.location.pathname;
	const stored = JSON.parse(sessionStorage.getItem(prefillKey) || '{}');
	const prefill = (key: string) => searchParams.get(key) || stored[key] || '';

	const { plan, setInstallmentCount } = useContext(PlanContext);
	const {
		content,
		currentPrice,
		setCurrentPrice,
		isLoading,
		billingType,
		setBillingType,
		checkout
	} = useContext(ContentContext);
	const { coupon } = useContext(CouponContext);
	const { orderBumps } = useContext(OrderBumpContext);
	const { setPayment } = useContext(PaymentContext);
	const { subscription, setSubscription } = useContext(SubscriptionContext);
	const { customization, finance } = useContext(ApplicationContext);

	// 🔴 `customization?.url` some quando a personalização não carrega — e some
	// justamente durante indisponibilidade, que é quando mais se precisa dela.
	// Interpolado direto num template, `undefined` vira a STRING "undefined" e
	// entra na URL: o comprador terminou de pagar e foi parar em
	// `/demo/p/undefined/p/payment/...` (medido em 09/09, log 197).
	//
	// Mesmo defeito do log 186, que eu corrigi só no caminho de ASSINATURA
	// (`resolveSubscriptionRedirect`) e não no irmão de venda avulsa, no mesmo
	// arquivo. Aqui a base fica em UMA constante para não haver terceiro caminho.
	const platformBaseUrl = customization?.url || window.location.origin;
	// Configurações da página (builder do hub): endereço por checkout.
	const pageSettings = useContext(CheckoutPageContext);
	const pageSlots = useContext(CheckoutPageSlotsContext);
	const hasConfirmationPage = useContext(CheckoutPageConfirmationContext);
	// Botões de ação: cor do botão (builder) > cor principal do tema
	// personalizado (texto automático) > cor da plataforma.
	const buttonColors = checkoutButtonColors({
		settings: pageSettings,
		platformPrimary: customization?.primaryColor,
		platformSecondary: customization?.secondaryColor
	});
	// O widget de garantia já carrega o selo de segurança — evita duplicar.
	const hasGuaranteeWidget = [
		...(pageSlots.aboveButton ?? []),
		...(pageSlots.belowButton ?? [])
	].some((b) => b.type === 'guarantee');
	const showAddress =
		pageSettings.fields?.address === 'hidden'
			? false
			: !!finance?.isAddressRequired;

	const { handle } = useSubmit();
	const queryClient = useQueryClient();

	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

	// O exit intent consulta esta marca para não abrir por cima de um pagamento
	// em processamento.
	useEffect(() => {
		if (isSubmitting) document.body.dataset[SUBMITTING_FLAG] = '1';
		else delete document.body.dataset[SUBMITTING_FLAG];
		return () => {
			delete document.body.dataset[SUBMITTING_FLAG];
		};
	}, [isSubmitting]);

	const createMemberApi = useMutation(membersApi.create);
	const openPaymentApi = useMutation(paymentsApi.open);
	const openSubscriptionApi = useMutation(subscriptionsApi.open);

	const getDefaultPhone = () => {
		const param = searchParams.get('phone');
		if (!param) {
			return '55';
		}

		if (param.startsWith('55')) {
			return param;
		}

		return '55' + param;
	};

	const methods = useForm<Inputs>({
		defaultValues: {
			phone: getDefaultPhone(),
			name: prefill('name'),
			email: prefill('email')
		}
	});

	/**
	 * Inteligência de conversão: foco/preenchimento por campo via delegação
	 * (um listener só) e erros de validação do react-hook-form. Nenhum valor
	 * digitado é enviado — só o NOME do campo.
	 */
	useEffect(() => {
		const onFocusIn = (event: FocusEvent) => {
			const target = event.target as HTMLInputElement | null;
			const name = target?.getAttribute?.('name');
			if (!name) return;
			trackBehavior('field_focus', name, undefined, { once: true });
		};
		const onFocusOut = (event: FocusEvent) => {
			const target = event.target as HTMLInputElement | null;
			const name = target?.getAttribute?.('name');
			if (!name) return;
			if (target?.value) {
				trackBehavior('field_filled', name, undefined, {
					once: true
				});
				if (name === 'email') identifyBuyer(target.value);
			}
		};
		document.addEventListener('focusin', onFocusIn);
		document.addEventListener('focusout', onFocusOut);
		return () => {
			document.removeEventListener('focusin', onFocusIn);
			document.removeEventListener('focusout', onFocusOut);
		};
	}, []);

	const isNameLocked = !!prefill('lockData') && !!prefill('name');
	const isEmailLocked = !!prefill('lockData') && !!prefill('email');

	useEffect(() => {
		const url = new URL(window.location.href);
		const keys = ['name', 'email', 'lockData'];
		if (!keys.some((key) => url.searchParams.has(key))) return;

		const snapshot: Record<string, string> = {};
		keys.forEach((key) => {
			const value = url.searchParams.get(key);
			if (value) snapshot[key] = value;
			url.searchParams.delete(key);
		});
		sessionStorage.setItem(prefillKey, JSON.stringify(snapshot));
		window.history.replaceState(null, '', url.pathname + url.search);
	}, []);
	const isForeign = methods.watch('isForeign');

	// Parcelas escolhidas → resumo (PlanContext). Sem isto o resumo mostrava o preço cheio
	// enquanto o seletor oferecia a 1x com o desconto à vista (pro_universo, 16/10/2026).
	const watchedInstallmentCount = methods.watch('installmentCount');
	useEffect(() => {
		setInstallmentCount?.(Number(watchedInstallmentCount) || 1);
	}, [watchedInstallmentCount]);

	const creditCard = methods.watch('creditCards')?.[0]?.creditCard;
	const userChangedCreditCard =
		typeof creditCard === 'object'
			? Object.values(creditCard)?.some(Boolean)
			: false;

	const {
		register,
		handleSubmit,
		getValues,
		formState: { errors },
		setValue,
		trigger
	} = methods;

	const errorFields = Object.keys(errors ?? {}).join(',');
	useEffect(() => {
		for (const field of errorFields.split(',').filter(Boolean)) {
			trackBehavior('field_error', field);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [errorFields]);

	const [validated, setValidated] = useState<boolean>(false);

	/**
	 * Layout em duas etapas (configurável por checkout no builder): etapa 1
	 * pede os dados, etapa 2 o pagamento. As seções são apenas OCULTADAS
	 * (`d-none`), nunca desmontadas — assim o estado do formulário, o
	 * turnstile e o submit continuam exatamente os mesmos do layout de uma
	 * etapa.
	 */
	const isTwoSteps = pageSettings.layout === 'steps';
	const [step, setStep] = useState<1 | 2>(1);

	const goToPaymentStep = async () => {
		const fields: (keyof Inputs | string)[] = [
			'name',
			'email',
			'cpfCnpj',
			...(pageSettings.fields?.phone === 'optional' ? [] : ['phone']),
			...(showAddress && !isForeign
				? [
						'address.postalcode',
						'address.address',
						'address.number',
						'address.province',
						'address.city',
						'address.state'
				  ]
				: [])
		];

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const isValid = await trigger(fields as any);
		if (!isValid) {
			setValidated(true);
			return;
		}
		setStep(2);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	const { beginCheckout, addPaymentInfo } = useGoogleAnalytics();

	const origin = useMemo(() => {
		if (!plan) return null;
		const originParam = searchParams.get('origin')?.toUpperCase();
		return originParam === 'COMERCIAL' ? 'COMERCIAL' : 'SELF_CHECKOUT';
	}, [plan, searchParams]);

	useEffect(() => {
		if (!currentPrice || !content?.pricing.price) return;

		const discountedPrice =
			content?.pricing.price - (coupon?.flatDiscount || 0);

		let price = discountedPrice;

		switch (billingType) {
			case BillingType.CreditCard: {
				const creditCardDiscount =
					content?.pricing.flatCreditCardDiscount || 0;
				price = discountedPrice - creditCardDiscount;
				break;
			}
			case BillingType.Pix: {
				const pixDiscount = content?.pricing.flatPixDiscount || 0;
				price = discountedPrice - pixDiscount;
				break;
			}
			case BillingType.Boleto: {
				const ticketDiscount = content?.pricing.flatTicketDiscount || 0;
				price = discountedPrice - ticketDiscount;
				break;
			}
		}

		if (price < 0) price = 0;

		setCurrentPrice(price);
	}, [billingType, content]);

	useEffect(() => {
		if (
			!content?.pricing.isInstallmentEnabled &&
			!((plan?.pricingSchema.maxInstallment || 1) > 1)
		) {
			return setShowInstallmentInput(false);
		}

		const isSmartInstallmentEnabledForPix =
			finance?.isBoletoNPixInstallmentEnabled &&
			content?.pricing.isSmartInstallmentEnabled;

		if (
			billingType !== BillingType.CreditCard &&
			!isSmartInstallmentEnabledForPix
		) {
			return setShowInstallmentInput(false);
		}

		return setShowInstallmentInput(true);
	}, [finance, content, plan, billingType]);

	const alreadySendedBeginCheckoutEvent = useRef(false);
	const alreadySendedAddPaymentInfoEvent = useRef(false);

	useEffect(() => {
		if (!currentPrice) return;

		alreadySendedBeginCheckoutEvent.current = false;
		alreadySendedAddPaymentInfoEvent.current = false;
	}, [currentPrice]);

	useEffect(() => {
		sendAddPaymentInfoEventToGa4();
	}, [userChangedCreditCard]);

	const handleNameAndEmailBlur = async () => {
		sendBeginCheckoutEventToGa4();

		const { email, name, phone } = getValues();

		if (!email || !name || !phone) {
			return;
		}

		const emailValidator =
			/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

		if (!email.match(emailValidator)) {
			toast.error('Email inválido');
			return;
		}

		if (phone.length !== 2) {
			const phoneValidator = /^\d{2} \d{2} \d{9}$/;

			if (!phone.match(phoneValidator)) {
				toast.error('telefone inválido');
				return;
			}
		}

		// Nunca lança: falha vai ao Sentry dentro dele e a compra segue.
		await handleMemberCreation();

		// `await` obrigatório: o submit chama esta função e segue direto pro
		// `handle()`. Sem esperar, o pagamento era aberto DEPOIS de o `handle()`
		// já ter procurado por ele — e o comprador levava "Pagamento não
		// encontrado" com a página recarregando.
		if (plan) {
			return { subscription: await handleSubscriptionOpening() };
		}

		return { payment: await handlePaymentOpening() };
	};

	/**
	 * A versão usada no `onBlur` dos campos — e SÓ nela.
	 *
	 * O blur é uma abertura ESPECULATIVA: adianta a criação do membro e do
	 * pagamento para o submit não pagar esse custo depois. Falhar aqui não impede
	 * a compra, porque o próprio submit chama `handleNameAndEmailBlur` de novo e
	 * usa o retorno.
	 *
	 * Só que `onBlur` não espera a promise: qualquer rejeição virava
	 * **unhandled rejection** — em dev, o overlay vermelho da CRA cobrindo o
	 * checkout a cada saída de campo; em produção, ruído sem dono. Aqui a falha
	 * é registrada e engolida DE PROPÓSITO.
	 *
	 * ⚠️ O caminho do submit continua usando `handleNameAndEmailBlur` direto,
	 * onde o erro TEM que subir — é dele que sai o `opened` que evita o
	 * "Pagamento não encontrado".
	 */
	const handleNameAndEmailBlurSafe = () => {
		handleNameAndEmailBlur().catch((error) => {
			console.warn(
				'[checkout] abertura especulativa no blur falhou — o submit refaz',
				error
			);
		});
	};

	const sendBeginCheckoutEventToGa4 = () => {
		if (alreadySendedBeginCheckoutEvent.current) return;

		beginCheckout({
			value: currentPrice || 0
		});
		alreadySendedBeginCheckoutEvent.current = true;
	};

	const sendAddPaymentInfoEventToGa4 = () => {
		if (alreadySendedAddPaymentInfoEvent.current) return;
		addPaymentInfo({
			value: currentPrice || 0
		});
		alreadySendedAddPaymentInfoEvent.current = true;
	};

	/**
	 * Membro já tratado nesta sessão do checkout (ver `memberSession.ts`).
	 * Guarda o token além do storage: o `cleanStorage()` do "Pagamento não
	 * encontrado" apaga o storage, e o próximo clique precisa dele de volta.
	 */
	const rememberedMember = useRef<RememberedMember | null>(null);
	/** Ordem do último `POST /user` emitido — resposta antiga é descartada. */
	const memberRequestSeq = useRef(0);
	/** `POST /user` no ar para um e-mail: quem chegar junto espera o mesmo. */
	const pendingMemberRequest = useRef<{
		email: string;
		promise: Promise<void>;
	} | null>(null);

	/**
	 * Nunca lança e nunca trava a compra: o `POST /customers` do pay resolve
	 * o comprador por conta própria. O que importa aqui é o `access_token`
	 * que vai no `#access_token=` do redirect — sem ele, o comprador novo
	 * chega à plataforma sem senha e sem conseguir entrar.
	 */
	const handleMemberCreation = async (): Promise<void> => {
		const { name, email, phone } = getValues();
		const normalizedEmail = normalizeEmail(email || '');

		// E-mail diferente do tratado (ou nenhum tratado ainda): o token
		// guardado é de outro comprador — ou de uma visita anterior — e não
		// pode viajar com esta compra. ANTES de qualquer retorno antecipado:
		// com o telefone reduzido ao DDI, o token de A sobrava na compra de B.
		if (!isSameMember(rememberedMember.current, email || '')) {
			localStorage.removeItem('access_token');
			rememberedMember.current = null;
		}

		if (!email || !name || !phone || phone.length === 2) return;

		// Um `POST /user` por e-mail: o blur já criou, o submit não cria de
		// novo — a repetição levava 409 e o token apagado antes não voltava.
		const remembered = rememberedMember.current;
		if (remembered) {
			if (remembered.accessToken) {
				localStorage.setItem('access_token', remembered.accessToken);
			}
			return;
		}

		// Pedido deste e-mail ainda no ar (clicar em "Finalizar compra" tira o
		// foco do campo: blur e submit chegam juntos): espera o mesmo em vez
		// de repetir — a repetição levava 409 e descartava o token do 1º.
		const pending = pendingMemberRequest.current;
		if (pending && pending.email === normalizedEmail) {
			await pending.promise;
			return handleMemberCreation();
		}

		const request: MemberRequest = {
			seq: ++memberRequestSeq.current,
			email: normalizedEmail
		};
		const promise = createMember(request, { name, email, phone });
		pendingMemberRequest.current = { email: normalizedEmail, promise };
		try {
			await promise;
		} finally {
			if (pendingMemberRequest.current?.promise === promise) {
				pendingMemberRequest.current = null;
			}
		}
	};

	/**
	 * A resposta só é aplicada se o pedido ainda é o mais recente E o e-mail
	 * da tela é o dele (`isCurrentMemberRequest`): os blurs não são esperados
	 * e o pedido de A pode resolver depois de o comprador trocar para B.
	 */
	const createMember = async (
		request: MemberRequest,
		input: { name: string; email: string; phone: string }
	): Promise<void> => {
		const isCurrent = () =>
			isCurrentMemberRequest(
				request,
				memberRequestSeq.current,
				getValues().email || ''
			);

		try {
			const { data } = await createMemberApi.mutateAsync(input);

			if (!isCurrent()) return;

			const accessToken = data?.access_token || null;

			if (accessToken) {
				localStorage.setItem('access_token', accessToken);
			} else {
				// Condição esperada (criação assíncrona): sem token na hora. A
				// plataforma mostra o login. Só diagnóstico, não vai ao Sentry.
				console.warn(
					'[checkout] member creation returned no access_token',
					{ platformUrl: customization?.url }
				);
			}

			rememberedMember.current = { email: request.email, accessToken };
		} catch (error) {
			if (isMemberAlreadyExistsError(error)) {
				// Membro existente: não há como logar sem senha, a plataforma
				// mostra o login. Lembra para não repetir o POST no submit.
				if (!isCurrent()) return;
				rememberedMember.current = {
					email: request.email,
					accessToken: null
				};
				return;
			}

			// Falha de servidor vai ao Sentry (#alertas-checkout); a compra
			// segue. Não lembra o e-mail: a próxima passagem tenta de novo.
			reportCheckoutError(error, 'customer');
		}
	};

	const handlePaymentOpening = async () => {
		const { email, phone } = getValues();

		if (!email || !content || !phone) {
			return;
		}

		const payment = await openPaymentApi.mutateAsync({
			email,
			contentId: content.id
		});
		setPayment(payment);

		// Devolve além de guardar no contexto: quem abre no meio do submit
		// precisa do valor AGORA, e `setPayment` só vale no próximo render.
		return payment;
	};

	const handleSubscriptionOpening = async () => {
		const { email } = getValues();

		if (!email && !plan?.id) return;

		try {
			const subscription = await openSubscriptionApi.mutateAsync({
				email,
				recurringPlanId: plan?.id as number,
				origin: location.toString()
			});
			setSubscription({ id: subscription.id });

			return { id: subscription.id };
		} catch (err) {
			// Antes: `false;` — a abertura da assinatura falhava e ninguém sabia.
			reportCheckoutError(err, 'subscription-open');
		}
	};

	const handleError = (message: string) => {
		trackBehavior(
			'payment_failed',
			undefined,
			(message || '').slice(0, 120)
		);
		toast.error(message);
		setIsSubmitting(false);
	};

	const cleanStorage = () => {
		localStorage.removeItem('access_token');
	};

	const { trackPurchase: trackPlatformPurchase } = useFbPixel();

	const checkoutHash = searchParams.get('hash') || undefined;

	const markCheckoutLinkUsed = async (referenceId: number | undefined) => {
		if (!checkoutHash || !checkout?.id || !referenceId) return;

		try {
			await checkoutsApi.useLink({
				checkoutId: checkout.id,
				hash: checkoutHash,
				referenceId
			});
		} catch (error) {
			// Non-blocking: marking the single-use link consumed must not
			// prevent the post-payment redirect. Surface to Sentry so we
			// can detect and reconcile orphaned ACTIVE links manually.
			Sentry.captureException(error, {
				level: 'warning',
				tags: {
					feature: 'checkout-single-use-link',
					platformUrl: customization?.url
				},
				extra: {
					checkoutId: checkout.id,
					hash: checkoutHash,
					referenceId
				}
			});
		}
	};

	const onSubmit = async (data: Inputs) => {
		trackBehavior('submit_attempt');
		try {
			setIsSubmitting(true);
			setValidated(true);

			const needsTurnstile = PATHNAMES_WITH_TURNSTILE.some(
				(pathname) =>
					window.location.pathname.includes(pathname) ||
					window.location.href.includes(pathname)
			);

			if (needsTurnstile && !turnstileToken) {
				toast.error('Por favor, complete a verificação de segurança');
				return;
			}

			const opened = await handleNameAndEmailBlur();

			const requestData = {
				...data,
				...(needsTurnstile &&
					turnstileToken && {
						sec: {
							turnstile: turnstileToken
						}
					})
			};

			const payment = await handle({
				billingType,
				...requestData,
				opened
			});

			// Valor da COMPRA para pixel, tracking e telas seguintes. Em assinatura
			// vem do pay (`chargedAmount`: cupom, desconto à vista e juros já
			// aplicados) — o `currentPrice` da tela não conhece o desconto à vista
			// nem os juros. Sem o campo (pay antigo), fica o da tela.
			const chargedAmount = Number(
				(payment as { chargedAmount?: number })?.chargedAmount
			);
			// Compra avulsa: o produto + os order bumps marcados, que entram na
			// MESMA cobrança — antes os pixels recebiam só o produto.
			const bumpsValue = plan
				? 0
				: orderBumps.reduce(
						(sum, bump) =>
							sum + (Number(bump.content.pricing?.price) || 0),
						0
				  );
			const purchaseValue =
				plan && Number.isFinite(chargedAmount) && chargedAmount > 0
					? chargedAmount
					: Math.round(((currentPrice || 0) + bumpsValue) * 100) /
					  100;

			// Assinatura no motor próprio devolve o DESFECHO da 1ª cobrança.
			// Recusa PARA AQUI: sem pixel de compra, sem redirect de sucesso —
			// antes o comprador via "sucesso" com o cartão recusado e o dunning
			// correndo em silêncio. Reenviar com outro cartão é o caminho: a
			// assinatura que pagar primeiro vence e a outra é encerrada como
			// duplicata pelo guard de liquidação do motor.
			const firstCharge = (
				payment as {
					firstCharge?: { status: string; reason?: string | null };
				}
			)?.firstCharge;

			if (firstCharge?.status === 'DECLINED') {
				setIsSubmitting(false);
				toast.error(
					'Pagamento recusado pelo emissor do cartão. Confira os dados ou tente outro cartão.'
				);
				return;
			}

			// 🔴 `PENDING` significa que a PRIMEIRA COBRANÇA NÃO ACONTECEU.
			//
			// Antes só `DECLINED` era tratado e todo o resto caía no caminho
			// feliz — então o comprador via a tela de obrigado sem ter pago, e
			// isso não gera erro para ninguém investigar. Medido no teste de
			// incidente de 08/09 (banco do tenant derrubado): o backend devolveu
			// 201 com `firstCharge: PENDING` e o checkout redirecionou.
			//
			// Nada reexecuta a cobrança na hora: o cron de ciclos roda 1x/dia às
			// 6h. Até lá a assinatura fica "Pendente" e o comprador acha que
			// comprou.
			//
			// ⚠️ ESCOLHA CONSERVADORA, sujeita a revisão do time. Aqui só se
			// garante o que é certamente correto: NÃO afirmar sucesso. As
			// alternativas discutidas — tela de espera reaproveitando o fluxo do
			// `RecurringPix` (que já aguarda `bill_paid`), ou redirecionar com
			// aviso — são decisões de produto e mudam o texto, não este ponto.
			//
			// `TRIAL` NÃO entra aqui de propósito: nele a ausência de cobrança é
			// legítima (há período de teste) e o sucesso é o desfecho certo.
			if (firstCharge?.status === 'PENDING') {
				setIsSubmitting(false);
				toast.error(
					'Ainda estamos confirmando o pagamento. Não recarregue a página — se a cobrança não for concluída em alguns minutos, tente novamente.'
				);
				return;
			}

			if (fixedTerm) {
				await signFixedPlatformTerm();
			} else if (shouldSignTerms) {
				await signPlatformTerm();
			}

			// Mark the single-use checkout link as consumed before
			// redirecting away. Errors are captured to Sentry inside the
			// helper and never propagate up — the redirect must always
			// proceed for paid customers.
			const referenceId = plan
				? opened?.subscription?.id ?? subscription?.id
				: (payment as { id?: number })?.id;
			await markCheckoutLinkUsed(referenceId);

			// Purchase is fired by the waiting screen, once `bill_paid` confirms.
			const isPixSubscription =
				!!plan &&
				(billingType === BillingType.Pix ||
					billingType === BillingType.PixAutomatic);

			trackBehavior('purchase');

			// Aprovado na hora: cartão ou gratuito (e, em assinatura, tudo o que
			// não é Pix). Pix e boleto só geraram a cobrança.
			const approvedOnTheSpot = plan
				? !isPixSubscription
				: billingType === BillingType.CreditCard || !currentPrice;

			// Pixel da plataforma: Purchase só do que já foi pago, só para ele e
			// com a referência como eventID (a mesma do obrigado do front-member).
			// Pix avulso e boleto saem no obrigado, quando o pagamento cair; Pix
			// de assinatura, na tela de espera.
			if (approvedOnTheSpot) {
				trackPlatformPurchase({
					value: purchaseValue,
					reference: payment?.reference
				});
			}

			// Pixels/analytics configurados por checkout no builder. Purchase só
			// com pagamento aprovado; Pix/boleto marcam a etapa e o Purchase sai
			// na confirmação (ver tracking.ts).
			trackPurchase({
				value: purchaseValue,
				reference: (payment as { reference?: string })?.reference,
				approved: approvedOnTheSpot
			});

			// Read the token AFTER handleNameAndEmailBlur() + handle(): those
			// create the member and persist its access_token to localStorage.
			// Reading it earlier (at the top of onSubmit) captured an empty token
			// for fresh buyers, so the pay-ui thank-you/payment pages never
			// auto-logged them in. It travels in the URL FRAGMENT, never the
			// query (see memberEntryUrl).
			const memberAccessToken = localStorage.getItem('access_token');

			// Checkout com `successRedirect`: aprovado, o comprador vai para a
			// tela de pagamento aprovado, que leva ao destino do lojista (botão
			// ou automático) — a URL vem pronta do pay. Sem a aprovação ainda
			// (cartão no motor do gateway), a mesma tela espera por ela.
			const successRedirect = getSuccessRedirectOptions(pageSettings);
			if (plan && successRedirect) {
				const successRedirectUrl = safeRedirectUrl(
					(payment as { successRedirectUrl?: string })
						?.successRedirectUrl
				);
				// Pix também passa pela tela de espera: guarda modo e texto.
				storeSuccessRedirect(payment.reference, {
					url: successRedirectUrl,
					...successRedirect
				});

				const successStep = resolveSuccessRedirectStep({
					successRedirectUrl,
					isPixSubscription,
					enabled: true,
					firstChargeStatus: firstCharge?.status
				});

				if (successStep) {
					const successParams = new URLSearchParams(
						window.location.search
					);
					successParams.set('plan_reference', plan.reference);
					successParams.set('value', String(purchaseValue));
					successParams.set('method', 'card');

					return navigate(
						`${platform ? `/${platform}` : ''}/p/recurring/pix/${
							payment.reference
						}?${successParams.toString()}`
					);
				}
			}

			// Not paid yet, so the buyer goes to the charge screen, not onward.
			if (isPixSubscription) {
				const pix = (payment as { pix?: unknown })?.pix;

				if (pix) {
					sessionStorage.setItem(
						`${PIX_CHARGE_STORAGE_PREFIX}${payment.reference}`,
						JSON.stringify(pix)
					);
				}

				const consentUrl = (
					payment as { pixAutomatic?: { consentUrl?: string } }
				)?.pixAutomatic?.consentUrl;

				if (consentUrl) {
					sessionStorage.setItem(
						`${PIX_CONSENT_STORAGE_PREFIX}${payment.reference}`,
						consentUrl
					);
				}

				const pixParams = new URLSearchParams(window.location.search);
				pixParams.set('plan_reference', plan.reference);
				pixParams.set('value', String(purchaseValue));
				// Records the method actually used, so the waiting screen does
				// not have to re-derive it from the incoming query.
				pixParams.set(
					'pix_mode',
					billingType === BillingType.PixAutomatic
						? 'automatic'
						: 'manual'
				);

				// Keeps the platform prefix: the waiting screen needs the slug to
				// resolve the api_key, the same way Recurring does.
				const platformPrefix = platform ? `/${platform}` : '';

				return navigate(
					`${platformPrefix}/p/recurring/pix/${
						payment.reference
					}?${pixParams.toString()}`
				);
			}

			// Só http(s): `javascript:` no `?redirect=` chegava ao `replace`.
			const redirect = safeExternalRedirect(searchParams.get('redirect'));
			if (redirect) return location.replace(redirect);

			if (plan) {
				const { url, isOnboarding } = resolveSubscriptionRedirect({
					planReference: plan.reference,
					receipt: payment.reference,
					value: purchaseValue,
					accessToken: localStorage.getItem('access_token') || '',
					platformUrl: customization?.url,
					isComercial: origin === 'COMERCIAL',
					sellerToken: searchParams.get('token')
				});

				if (isOnboarding) {
					toast.success(
						'Compra realizada com sucesso! Redirecionado para onboarding...'
					);
					setTimeout(() => location.replace(url), 2000);
					return;
				}

				return location.replace(url);
			}

			// Página de confirmação montada no builder: fica no próprio
			// checkout. Sem ela, segue o obrigado da plataforma.
			//
			// Só para o que foi APROVADO NA HORA (cartão ou gratuito). Pix e
			// boleto ainda precisam ser pagos, e quem mostra o QR Code e o
			// boleto é a tela de pagamento da plataforma — a confirmação do
			// builder dizia "Compra confirmada!" e o comprador ficava sem ter
			// como pagar.
			if (hasConfirmationPage && approvedOnTheSpot) {
				const thanksPath = window.location.pathname.replace(
					'/p/',
					'/p/thanks/'
				);
				const thanksParams = new URLSearchParams();
				if (payment?.reference)
					thanksParams.set('receipt', payment.reference);
				// Order bumps marcados entram no "Seu pedido" do obrigado (o
				// `v` abaixo é só o produto). Mesmo armazenamento das ofertas
				// pós-compra, preso ao comprovante desta compra.
				for (const bump of orderBumps) {
					addOrderExtra(thanksPath, payment?.reference ?? null, {
						kind: 'orderBump',
						title: bump.content.title,
						amount:
							Number(bump.content.pricing?.price) >= 0
								? Number(bump.content.pricing?.price)
								: null
					});
				}
				// Valor para os pixels da página de obrigado.
				// Produto + bumps: é o valor dos pixels de compra do obrigado,
				// e o resumo separa o produto dos bumps.
				if (purchaseValue) thanksParams.set('v', String(purchaseValue));
				const currentParams = new URLSearchParams(
					window.location.search
				);
				const apiKey = currentParams.get('api_key');
				if (apiKey) thanksParams.set('api_key', apiKey);

				// Upsell one-click: compra no cartão aprovada + oferta
				// configurada → desvia para a página de upsell antes do
				// obrigado. O token curto vai por sessionStorage.
				const upsellConfig = pageSettings?.upsell;
				const upsellToken = (payment as { upsellToken?: string })
					?.upsellToken;
				if (
					upsellConfig?.enabled &&
					upsellConfig?.contentId &&
					upsellToken
				) {
					const upsellPath = window.location.pathname.replace(
						'/p/',
						'/p/upsell/'
					);
					try {
						const buyer = getValues();
						sessionStorage.setItem(
							`checkout-upsell:${upsellPath}`,
							JSON.stringify({
								token: upsellToken,
								thanksQuery: thanksParams.toString()
							})
						);
						sessionStorage.setItem(
							`checkout-thanks-buyer:${thanksPath}`,
							JSON.stringify({
								email: buyer.email || '',
								name: buyer.name || '',
								// Para o botão "Acessar a plataforma" entregar o
								// comprador já logado (ver platformEntryUrl).
								accessToken:
									localStorage.getItem('access_token') || ''
							})
						);
						return location.replace(
							`${upsellPath}?${thanksParams.toString()}`
						);
					} catch {
						// sem storage não há como levar o token — segue
						// direto para o obrigado.
					}
				}
				// UTMs seguem para a página de obrigado (e de lá para o link
				// do botão, se o admin usar as variáveis).
				currentParams.forEach((value, key) => {
					if (key.toLowerCase().startsWith('utm_')) {
						thanksParams.set(key, value);
					}
				});
				// Dados do comprador para as variáveis do link do botão
				// ({{email}}, {{nome}}) — sessionStorage, não URL (sem PII).
				try {
					const buyer = getValues();
					sessionStorage.setItem(
						`checkout-thanks-buyer:${thanksPath}`,
						JSON.stringify({
							email: buyer.email || '',
							name: buyer.name || '',
							accessToken:
								localStorage.getItem('access_token') || ''
						})
					);
				} catch {
					// storage indisponível não pode travar o redirect
				}
				return location.replace(
					`${thanksPath}?${thanksParams.toString()}`
				);
			}

			return location.replace(
				withSessionFragment(
					`${platformBaseUrl}/p/payment/${payment?.reference}`,
					memberAccessToken
				)
			);
		} catch (err) {
			// O `message` do backend nem sempre é string (array do
			// ValidationPipe, objeto `{ pt, en }` do 404 da abertura). Passar
			// direto pro toast dava toast vazio — o comprador clicava e não via
			// nada (forumiep, WAID-4992).
			if (axios.isAxiosError(err) && isSellerNotActiveError(err)) {
				// A plataforma deixou de poder receber com a página aberta (409
				// do pay #619): estado de negócio, não erro do servidor — não
				// vai ao Sentry. Em vez de um toast que deixa o formulário na
				// tela (e o comprador tentando de novo), a página inteira vira
				// o estado "pagamentos indisponíveis".
				trackBehavior('payment_failed', undefined, 'seller_not_active');
				setIsSubmitting(false);
				markPaymentsUnavailable(queryClient);
				return;
			}

			if (axios.isAxiosError(err)) {
				const message = getApiErrorMessage(
					err,
					'Não foi possível iniciar o pagamento. Confira seus dados e tente novamente.'
				);
				// Erro do servidor no envio vai ao Sentry (#alertas-checkout); antes era só toast.
				reportCheckoutSubmitError(err, message);
				// Recusa que aponta um campo: marca o campo, para o comprador saber o que corrigir.
				// Só se o campo está na tela; senão o toast (que já traz "Confira o campo …") basta.
				const fieldError = apiFieldErrorOf(err);
				if (
					fieldError &&
					isFieldMounted(methods.control._fields, fieldError.field)
				) {
					methods.setError(fieldError.field, {
						type: 'server',
						message: fieldError.message
					});
					methods.setFocus(fieldError.field);
				}
				return handleError(message);
			}

			if (err instanceof Error) {
				if (err.message === Errors.PAYMENT_NOT_FOUND) {
					handleError(
						'Pagamento não encontrado. Tente novamente, por favor.'
					);
					// Sem `location.reload()`: o reload matava o toast em
					// milissegundos e o comprador ficava sem saber o que houve.
					// O próximo clique em "Finalizar compra" reabre o pagamento
					// sozinho (o submit chama `handleNameAndEmailBlur` de novo).
					cleanStorage();
					return;
				}

				// Validação da tela ou recusa do cartão já embrulhada: esperado, não vai ao Sentry.
				return handleError(err.message);
			}

			reportCheckoutSubmitError(
				err,
				'Um erro desconhecido ocorreu. Tente novamente mais tarde.'
			);
			return handleError(
				'Um erro desconhecido ocorreu. Tente novamente mais tarde.'
			);
		}
	};

	const loadedTurnstile = useRef(false);
	const [turnstileToken, setTurnstileToken] = useState<string>('');
	useEffect(() => {
		if (loadedTurnstile.current) {
			return;
		}

		// eslint-disable-next-line
		const w = window as any;

		if (w.onloadTurnstileCallback) {
			return;
		}

		const key = process.env.REACT_APP_TURNSTILE_SITE_KEY;

		if (!key) {
			console.warn('Skipping captcha because of missing key');
			return;
		}

		if (
			!PATHNAMES_WITH_TURNSTILE.some(
				(pathname) =>
					window.location.pathname.includes(pathname) ||
					window.location.href.includes(pathname)
			)
		) {
			return;
		}

		// The Turnstile script (public/index.html) is loaded from an external
		// Cloudflare CDN, so `w.turnstile` may not be available yet when this
		// effect runs (slow network, ad-blocker, corporate proxy). Wait for it
		// to be ready before accessing it, otherwise `w.turnstile.ready` throws
		// "Cannot read properties of undefined (reading 'ready')".
		const startTurnstile = () => {
			w.turnstile.ready(async function () {
				const container = await new Promise((resolve) => {
					const interval = setInterval(() => {
						const container = document.querySelector('#turnstile');

						if (container) {
							clearInterval(interval);
							resolve(container);
						}
					}, 1000);
				});

				w.turnstile.render(container, {
					sitekey: key,
					callback: function (token: string) {
						loadedTurnstile.current = true;
						setTurnstileToken(token);
					},
					'expired-callback': function () {
						setTurnstileToken('');
					}
				});
			});
		};

		if (w.turnstile) {
			startTurnstile();
		} else {
			// Poll for the external script to finish loading before using it,
			// giving up after ~15s so we never leak the interval.
			let attempts = 0;
			const readyInterval = setInterval(() => {
				attempts += 1;

				if (w.turnstile) {
					clearInterval(readyInterval);
					startTurnstile();
				} else if (attempts >= 30) {
					clearInterval(readyInterval);
					console.warn('Turnstile script failed to load');
				}
			}, 500);
		}
	}, []);

	const [apiKey, setApiKey] = useState('');
	useEffect(() => {
		if (apiKeyProp) {
			setApiKey(apiKeyProp);
		} else {
			apiFactory.getApiKey().then(setApiKey);
		}
	}, [apiKeyProp]);

	const fixedTerm = FIXED_TERM_PLATFORMS[apiKey];
	const isDarkTheme = useIsDarkMode();

	const signFixedPlatformTerm = async () => {
		if (!fixedTerm) return;

		const { name, email } = getValues();
		const accessToken = localStorage.getItem('access_token');

		try {
			// Both branches must record the term the buyer actually read — the
			// fixed PDF above — so each identifies fixedTerm.uuid explicitly.
			// /terms-of-use/sign is deliberately NOT used here: it resolves the
			// term itself via findCurrent and ignores any uuid in the body,
			// which would attribute the signature to whatever version happens
			// to be current for the application.
			if (accessToken) {
				await axios.post(
					`${process.env.REACT_APP_CURSEDUCA_PROF_DOMAIN}/terms-of-use/${fixedTerm.uuid}/signature`,
					{},
					{
						headers: {
							api_key: apiKey,
							Authorization: `Bearer ${accessToken}`
						}
					}
				);
			} else {
				await axios.post(
					`${process.env.REACT_APP_CURSEDUCA_PROF_DOMAIN}/terms-of-use/sign-anonymous`,
					{ termOfUseUuid: fixedTerm.uuid, email, name },
					{ headers: { api_key: apiKey } }
				);
			}
		} catch (error) {
			// Non-blocking: term signing failure should not prevent payment completion
			Sentry.captureException(error, {
				level: 'warning',
				tags: { platformUrl: customization?.url }
			});
		}
	};

	const signPlatformTerm = async () => {
		const { name, email } = getValues();
		const accessToken = localStorage.getItem('access_token');

		try {
			if (accessToken) {
				await axios.post(
					`${process.env.REACT_APP_CURSEDUCA_PROF_DOMAIN}/terms-of-use/sign`,
					{},
					{
						headers: {
							api_key: apiKey,
							Authorization: `Bearer ${accessToken}`
						}
					}
				);
			} else {
				const { data: currentTerm } = await axios.get<{ uuid: string }>(
					`${process.env.REACT_APP_CURSEDUCA_PROF_DOMAIN}/terms-of-use/current`,
					{ headers: { api_key: apiKey } }
				);

				await axios.post(
					`${process.env.REACT_APP_CURSEDUCA_PROF_DOMAIN}/terms-of-use/sign-anonymous`,
					{ termOfUseUuid: currentTerm.uuid, email, name },
					{ headers: { api_key: apiKey } }
				);
			}
		} catch (error) {
			// Non-blocking: term signing failure should not prevent payment completion
			Sentry.captureException(error, {
				level: 'warning',
				tags: { platformUrl: customization?.url }
			});
		}
	};

	const isSelfCheckout = !!CURSEDUCA_PLANS_REFERENCES[plan?.reference || ''];

	const shouldSignTerms =
		!!finance?.shouldDisplayTerms || isSelfCheckout || !!fixedTerm;

	return (
		<FormProvider {...methods}>
			<BsForm
				noValidate
				validated={validated}
				onSubmit={handleSubmit(onSubmit)}
			>
				<Card.Body className="card-body">
					{isLoading ? (
						<CheckoutFormSkeleton />
					) : (
						<>
							{isTwoSteps && (
								<div className="checkout-steps">
									<span
										className={
											step === 1
												? 'checkout-step is-active'
												: 'checkout-step is-done'
										}
									>
										<b>1</b> Seus dados
									</span>
									<span className="checkout-step-divider" />
									<span
										className={
											step === 2
												? 'checkout-step is-active'
												: 'checkout-step'
										}
									>
										<b>2</b> Pagamento
									</span>
								</div>
							)}

							<div
								className={
									isTwoSteps && step !== 1 ? 'd-none' : ''
								}
							>
								{/* Sem margem no primeiro título: alinha com o
								    topo do resumo (logo) na coluna ao lado. */}
								<Card.Title className="card-title mt-0">
									<h3 className="card-label h4 text-gray-800">
										Informações pessoais{' '}
									</h3>
								</Card.Title>
								<Row>
									<Col className="mb-5" lg={12}>
										<Tooltip
											title={
												isNameLocked
													? 'Dados pré-preenchidos pela plataforma e não editáveis.'
													: ''
											}
										>
											<TextField
												label="Nome completo"
												className="w-100"
												required
												type="text"
												{...register('name', {
													required:
														'O campo de nome é obrigatório!',
													maxLength: {
														message:
															'O campo de nome deve conter até no máximo 64 caracteres!',
														value: 64
													}
												})}
												placeholder="Francisco Silva"
												error={!!errors.name}
												helperText={
													errors.name?.message
												}
												onBlur={
													handleNameAndEmailBlurSafe
												}
												InputProps={{
													readOnly: isNameLocked
												}}
											/>
										</Tooltip>
									</Col>
									<Col className="mb-5 pr-md-1" lg={8}>
										<Tooltip
											title={
												isEmailLocked
													? 'Dados pré-preenchidos pela plataforma e não editáveis.'
													: ''
											}
										>
											<TextField
												label="Email"
												className="w-100 "
												required
												sx={{
													borderRadius: 89
												}}
												type="email"
												{...register('email', {
													required:
														'O campo de email é obrigatório.',
													maxLength: {
														message:
															'O campo de nome deve conter até no máximo 100 caracteres!',
														value: 100
													},
													pattern: {
														value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
														message:
															'Email inválido'
													}
												})}
												placeholder="exemplo@dominio.com"
												error={!!errors.email}
												helperText={
													errors.email?.message
												}
												onBlur={
													handleNameAndEmailBlurSafe
												}
												InputProps={{
													readOnly: isEmailLocked
												}}
											/>
										</Tooltip>
									</Col>

									<Col className="mb-5 pl-md-1" lg={4}>
										<PhoneInput
											required
											className="w-100"
											type="text"
											{...register('phone', {
												required:
													'O campo de celular é obrigatório!',
												pattern: {
													value: /^[0-9]{2} [0-9]{2} 9[0-9]{8}$/,
													message: 'Celular inválido'
												}
											})}
											error={!!errors.phone}
											helperText={errors.phone?.message}
											onBlur={handleNameAndEmailBlurSafe}
										/>
									</Col>
								</Row>

								<Row className="align-items-center">
									{finance?.isAntifraudEnabled === false && (
										<Col className="d-none mb-5" xs={5}>
											<Checkbox
												label="Sou estrangeiro"
												{...register('isForeign')}
											/>
										</Col>
									)}
									<Col className="mb-5" xs={12}>
										<CpfCnpjInput
											{...register('cpfCnpj', {
												required:
													'O campo de documento é obrigatório!',
												validate: (value) =>
													isForeign ||
													isValidDocument(value)
											})}
											onBlur={handleNameAndEmailBlurSafe}
											error={!!errors.cpfCnpj}
											helperText={
												errors.cpfCnpj?.type ===
												'validate'
													? 'Documento inválido'
													: errors.cpfCnpj?.message
											}
											isForeign={isForeign}
											required
										/>
									</Col>
								</Row>

								<div id="turnstile"></div>

								{showAddress && !isForeign && <AddressForm />}

								{isTwoSteps && step === 1 && (
									<Button
										size="lg"
										type="button"
										onClick={goToPaymentStep}
										className="d-block mx-auto mt-5 mt-md-8 border-0 w-100 max-w-450px font-weight-bold rounded-lg py-5 bg-hover-scaleup h5"
										style={buttonColors}
									>
										Continuar para o pagamento
									</Button>
								)}
							</div>

							<div
								className={
									isTwoSteps && step !== 2 ? 'd-none' : ''
								}
							>
								{isTwoSteps && step === 2 && (
									<button
										type="button"
										className="checkout-step-back"
										onClick={() => setStep(1)}
									>
										← Voltar aos meus dados
									</button>
								)}
								<div>
									{(currentPrice || plan?.price) && (
										<>
											<BillingTypeForm
												setBillingType={setBillingType}
											/>
											<Row>
												<Col
													md={12}
													className={`${
														shouldShowInstallmentInput
															? ''
															: 'd-none'
													}`}
												>
													<BsForm.Group>
														<InstallmentsInput
															setValue={(
																value: number
															) =>
																setValue(
																	`installmentCount`,
																	value
																)
															}
															{...register(
																`installmentCount`,
																{
																	value: 1,
																	valueAsNumber:
																		true
																}
															)}
															maxInstallment={
																plan
																	? plan
																			.pricingSchema
																			.maxInstallment
																	: content
																			?.pricing
																			.maxInstallment
															}
															isInvalid={
																!!errors.installmentCount
															}
														/>
														{content?.pricing
															.isSmartInstallmentEnabled ? (
															<span className="d-block mt-2 font-weight-bold text-muted">
																Esta compra será
																feita com
																parcelamento
																inteligente, em
																que o valor
																total do produto
																será dividido em
																mensalidades sem
																bloquear o
																limite no seu
																cartão de
																crédito.
															</span>
														) : (
															''
														)}
													</BsForm.Group>
												</Col>
											</Row>
										</>
									)}

									{shouldSignTerms && (
										<>
											<label className="checkbox">
												<input
													type="checkbox"
													onChange={(e) =>
														setHasSignedTerms(
															e.target.checked
														)
													}
												/>
												<span
													style={{
														minWidth: '18px'
													}}
													className="mr-3"
												></span>
												{isSelfCheckout ? (
													<p
														className="mb-0 font-size-lg"
														style={{
															color: isDarkTheme
																? '#fff'
																: 'inherit'
														}}
													>
														Eu li e concordo com os{' '}
														<a
															target="_blank"
															href="https://legal.waid.io"
															rel="noreferrer"
															className="font-weighti-bolder"
														>
															termos de uso e
															políticas de
															privacidade
														</a>
													</p>
												) : (
													<p
														className="mb-0 font-size-lg"
														style={{
															color: isDarkTheme
																? '#fff'
																: 'inherit'
														}}
													>
														Eu li e concordo com os{' '}
														<a
															target="_blank"
															href={
																fixedTerm
																	? fixedTerm.url
																	: `${platformBaseUrl}/terms`
															}
															rel="noreferrer"
															className="font-weight-bolder"
														>
															termos de uso
														</a>{' '}
														e{' '}
														<a
															target="_blank"
															href={`${platformBaseUrl}/privacy`}
															rel="noreferrer"
															className="font-weight-bolder"
														>
															políticas de
															privacidade
														</a>
													</p>
												)}
											</label>
										</>
									)}

									<div className="checkout-submit-area">
										<PageSlotBlocks slot="aboveButton" />

										<OrderBump />

										<Button
											size="lg"
											type="submit"
											className="d-block mx-auto mt-5 mt-md-8 border-0 w-100 max-w-450px font-weight-bold rounded-lg py-5 bg-hover-scaleup h5"
											style={buttonColors}
											disabled={
												isSubmitting ||
												(!hasSignedTerms &&
													shouldSignTerms)
											}
										>
											{isSubmitting && (
												<Spinner
													animation="border"
													size="sm"
													as="span"
													role="status"
													aria-hidden="true"
													className="mr-1 mb-1"
												/>
											)}
											{pageSettings.buttonLabel ||
												(currentPrice || plan?.price
													? 'Finalizar compra'
													: 'Acessar')}
										</Button>

										<PageSlotBlocks slot="belowButton" />
									</div>
								</div>
							</div>
						</>
					)}
				</Card.Body>
				{!hasGuaranteeWidget && (
					<div className="d-flex align-items-center justify-content-center justify-self-end mb-5 text-center checkout-success">
						<span className="svg-icon svg-icon-lg checkout-success mr-2">
							<ShieldCheck weight="duotone" />
						</span>
						<span className="font-size-lg">Ambiente seguro</span>
					</div>
				)}
				<div className="d-block d-md-none justify-self-end mb-5 text-center text-muted">
					Powered by <span className="font-weight-bolder">Waid</span>
				</div>
			</BsForm>
		</FormProvider>
	);
};
